/* eslint-disable no-param-reassign, no-underscore-dangle */
// @flow
import type {Set} from 'immutable';
import {fromJS, List, Map} from 'immutable';
import moment from 'moment';
import type {QuestionSetModelV1} from 'resources/GeneratedModels/QuestionSet/QuestionSetModel.v1';
import type {StudentModel} from 'resources/Student/Student.model';

import {Store} from 'client/framework';
import errorCodes from 'client/errorCodes';

import appStore from 'client/AppStore';
import assignmentsListStore from 'client/Classrooms/Classroom/Teacher/Assignments/AssignmentsList/AssignmentsList.store';
import masqueradeStore from 'generic/Masquerade/Masquerade.store';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';
import analytics from 'client/Analytics';
import notifier from '@albert-io/notifier';
import {resource, query} from '@albert-io/json-api-framework/request/builder';
import {AssignmentModelV3} from 'resources/augmented/Assignment/AssignmentModel.v3';
import {SETTINGS as correctAnswerSettings} from 'client/Assignments/constants';
import {genericMandarkRequest, invalidatePartialInterest} from 'resources/mandark.resource';
import type {QueryBuilder} from '@albert-io/json-api-framework/request/builder/legacy';
import type {ClassroomModelV1} from 'resources/augmented/Classroom/ClassroomModel.v1';

import {invalidateFilters} from 'components/PracticeView/PracticeViewToolbar/Filters/filters.utils';

import {invalidateTeacherAssignmentStats} from '../../framework/MandarkAPI/Assignments/Assignments';

import createAssignmentActions from './CreateAssignment.actions';

const inputKeys = ['due_date', 'name', 'start_date', 'start_time', 'time_limit'];

/*
  We link the names of our form's inputs to the setter and getter
   methods of the assignment model. This reduces redundancy
  in our _setFieldState and _validateAssignment methods because we can
   call exactly the right method on the assignment model for a given input name.
*/
const modelMethods = fromJS({
  /* eslint-disable camelcase */
  allow_late_submissions: {
    set: 'setAllowLateSubmissions',
    get: 'isAllowLateSubmissions'
  },
  correct_answer_setting: {
    set: 'setCorrectAnswerSetting',
    get: 'getCorrectAnswerSetting'
  },
  due_date: {
    set: 'setDueDate',
    get: 'getDueDate'
  },
  message: {
    set: 'setMessage',
    get: 'getMessage'
  },
  name: {
    set: 'setName',
    get: 'getName'
  },
  randomize_question_order: {
    set: 'setRandomizeQuestionOrder',
    get: 'isRandomizeQuestionOrder'
  },
  start_date: {
    set: 'setStartDate',
    get: 'getStartDate'
  },
  start_time: {
    set: 'setStartTime',
    get: 'getStartTime'
  },
  time_limit: {
    set: 'setTimeLimit',
    get: 'getTimeLimit'
  }
  /* eslint-enable camelcase */
});

class CreateAssignmentStore extends Store {
  constructor(name: string) {
    super(name);

    this.initialData = fromJS({
      assignment: null,
      activeRecipientGroup: null,
      breadcrumbs: [],
      breadcrumbLabel: 'Source',
      errorData: {},
      hasReceivedAssignmentData: false,
      isAssignmentValid: false,
      isEditMode: false,
      isSavePending: false,
      isSaveSuccessful: false,
      originalAssignment: null,
      questionCount: null,
      questionDifficultyTotals: {},
      questionSetList: [],
      promiseResponse: null,
      savedAssignmentPayload: [],
      selectedRecipients: {},
      toastMessage: [],
      showOutOfScopeSubjectModal: false,
      isAddSubjectsToOutOfScopeClassroomsPending: false,
      addOutOfScopeSubjectsToClassroomsPromise: null,
      sections: []
    });

    this.setInitialData(this.initialData);
    this.handle(createAssignmentActions.ADD_SINGLE_RECIPIENT, this._addSingleRecipient);
    this.handle(createAssignmentActions.SAVE_ASSIGNMENT, this._saveAssignment);
    this.handle(createAssignmentActions.LAUNCH_ASSIGNMENT_CREATOR, this._launchAssignmentCreator);
    this.handle(createAssignmentActions.REMOVE_SINGLE_RECIPIENT, this._removeSingleRecipient);
    this.handle(createAssignmentActions.RESET_STORE, this._resetStore);
    this.handle(createAssignmentActions.RESET_TOAST_MESSAGE, this._resetToastMessage);
    this.handle(createAssignmentActions.SET_ACTIVE_RECIPIENT_GROUP, this._setActiveRecipientGroup);
    this.handle(createAssignmentActions.SET_FIELD_STATE, this._setFieldState);
    this.handle(createAssignmentActions.SET_MULTIPLE_RECIPIENTS, this._setMultipleRecipients);
    this.handle(
      createAssignmentActions.SET_SAVED_ASSIGNMENT_PAYLOAD,
      this._setSavedAssignmentPayload
    );
    this.handle(createAssignmentActions.SYNC_ROSTERS, this._syncRosters);
    this.handle(
      createAssignmentActions.TOGGLE_AUTO_ASSIGN_NEW_CLASSROOM_ENROLLEES,
      this._toggleAutoAssignNewEnrollees
    );
    this.handle(createAssignmentActions.VALIDATE_ASSIGNMENT, this._validateAssignment);
    this.handle(
      createAssignmentActions.SET_SHOW_OUT_OF_SCOPE_SUBJECT_MODAL,
      this.setProperty('showOutOfScopeSubjectModal')
    );
    this.handle(
      createAssignmentActions.ADD_OUT_OF_SCOPE_SUBJECTS_TO_CLASSROOMS,
      this._addOutOfScopeSubjectsToClassrooms
    );
    this.handle(createAssignmentActions.UPDATE_SECTIONS, this.setProperty('sections'));
  }

  /*
    ASSIGNMENT MODEL HELPERS
  */
  getAssignmentModel(): AssignmentModelV3 {
    return this.readData('assignment');
  }

  getSections() {
    return this.readData('sections');
  }

  getDefaultSections() {
    return this.getQueryForAssessment().getResource().getSortedSections();
  }

  /*
    LAUNCH_ASSIGNMENT_CREATOR
  */
  _launchAssignmentCreator({
    assignment,
    assignmentMetadata,
    isEditMode = false
  }: {
    assignment: AssignmentModelV3,
    assignmentMetadata: Map<*, *>,
    isEditMode: boolean
  }) {
    assignment =
      assignment ||
      AssignmentModelV3.getDefaultModel().setDueDate(null).setStartDate(null).setTimeLimit(null);
    this.writeData((store) => {
      if (assignment.getCorrectAnswerSetting() === '') {
        const defaultSetting = this.isAssigningPracticeExam()
          ? correctAnswerSettings.SHOW_AFTER_ALL
          : correctAnswerSettings.SHOW_AFTER_EACH;
        assignment = assignment.setCorrectAnswerSetting(defaultSetting);
      }

      store = store
        .merge(assignmentMetadata)
        .set('isEditMode', isEditMode)
        .set('hasReceivedAssignmentData', true);

      if (this.isAssigningPracticeExam()) {
        store = store.set('sections', this.getDefaultSections());
      }

      // edit mode should hydrate form with the classroom assignment settings
      if (isEditMode) {
        const {classroomId} = appStore.routerProps.params;
        const classroomAssignment = this.getClassroomAssignmentByClassroomId(
          assignment,
          classroomId
        );

        const settings = classroomAssignment.getSettings();

        if (settings) {
          assignment = assignment
            .setAllowLateSubmissions(settings.get('allow_late_submissions'))
            .setAutoAssignNewClassroomEnrollees(settings.get('auto_assign_new_classroom_enrollees'))
            .setCorrectAnswerSetting(settings.get('correct_answer_setting'))
            .setDueDate(settings.get('due_date'))
            .setMessage(settings.get('message'))
            .setStartDate(settings.get('start_date'))
            .setTimeLimit(settings.get('time_limit'))
            .setRandomizeQuestionOrder(settings.get('randomize_question_order'));
        }
      }

      store = store.set('assignment', assignment).set('originalAssignment', assignment);

      return store;
    });
  }
  /*
    LAUNCH_ASSIGNMENT_CREATOR HELPERS
  */

  getBreadcrumbs(): List<*> {
    return this.readData('breadcrumbs');
  }

  getBreadcrumbLabel(): List<*> {
    return this.readData('breadcrumbLabel');
  }

  getQuestionSetList(): List<QuestionSetModelV1> {
    return this.readData('questionSetList');
  }

  getQuestionCount(): number {
    return this.readData('questionCount');
  }

  getTotalEasyQuestions(): number {
    return this.readData('questionDifficultyTotals').get('1', new List()).size;
  }

  getTotalModerateQuestions(): number {
    return this.readData('questionDifficultyTotals').get('2', new List()).size;
  }

  getTotalDifficultQuestions(): number {
    return this.readData('questionDifficultyTotals').get('3', new List()).size;
  }

  getCurrentStep(): number {
    return this.readData('currentStep');
  }

  /*
    LOAD SELECT RECIPIENTS PAGE
  */
  getTeacherId(): string {
    return masqueradeStore.getUserIdByMasqueradeState();
  }

  _getClassroomsQuery(): QueryBuilder {
    return mandarkEndpoint(['teachers_v1', this.getTeacherId(), 'classrooms_v1'])
      .include('students_v1')
      .include('subjects_v1')
      .filter({
        status: 'active'
      })
      .pageSize(250);
  }

  getMyClassrooms(): List<Map<string, *>> {
    return this._getClassroomsQuery()
      .getResource()
      .sortBy((classroom) => classroom.getName().toLowerCase())
      .toList();
  }

  getClassroomAssignmentByClassroomId(
    assignment: AssignmentModelV3,
    classroomId: string
  ): Map<string, *> {
    const classroom = assignment
      .getClassrooms()
      .filter((cr) => cr.get('id') === classroomId)
      .first();

    const classroomAssignment = classroom.getAssignmentRelationships(assignment.get('id'));

    return classroomAssignment;
  }

  hasReceivedMyClassrooms(): boolean {
    return this._getClassroomsQuery().isResourceReady();
  }

  /*
    SELECT RECIPIENTS PAGE HELPERS
  */
  _addSingleRecipient(recipientData: Map<string, *>) {
    const selectedRecipients = this.getSelectedRecipients();
    const classroomId = recipientData.get('classroomId');
    const classroomName = recipientData.get('classroomName');
    const student = recipientData.get('student');

    /*
      If the group already exists, push the current value to it. If not,
      wrap the new value in an Immutable list before setting it (or else
      the `remove` functionality will break)
    */
    const updatedSelectedRecipients = selectedRecipients
      .updateIn([classroomId, 'students'], new List(), (list) => list.push(student))
      .setIn([classroomId, 'name'], classroomName);

    this.writeData('selectedRecipients', updatedSelectedRecipients);
  }

  _removeSingleRecipient(recipientData: Map<string, *>) {
    const classroomId = recipientData.get('classroomId');
    const student = recipientData.get('student');

    const selectedRecipients = this.getSelectedRecipients();

    /*
      Find the index of the current id and delete it from the group node.
    */
    const indexOfRecipientId = selectedRecipients
      .getIn([classroomId, 'students'])
      .findIndex((recipient) => {
        return recipient.getId() === student.getId();
      });

    let updatedSelectedRecipients;

    updatedSelectedRecipients = selectedRecipients.updateIn([classroomId, 'students'], (list) =>
      list.delete(indexOfRecipientId)
    );

    if (updatedSelectedRecipients.getIn([classroomId, 'students']).isEmpty()) {
      updatedSelectedRecipients = updatedSelectedRecipients.delete(classroomId);
    }

    this.writeData('selectedRecipients', updatedSelectedRecipients);
  }

  _setMultipleRecipients(recipientData: Map<string, *>) {
    const selectedRecipients = this.getSelectedRecipients();
    const addRecipients = recipientData.get('addRecipients');
    const classroomId = recipientData.get('classroomId');
    const classroomName = recipientData.get('classroomName');
    const students = recipientData.get('students');
    const autoAssignNewEnrollees = recipientData.get('autoAssignNewEnrollees');
    /*
      If addRecipients is true, create a new node
      for this classroom and add its recipients. If false,
      remove the node.
    */
    let updatedSelectedRecipients;

    updatedSelectedRecipients = selectedRecipients.update(classroomId, new Map(), (classroom) => {
      const updateType = addRecipients ? 'set' : 'delete';
      return classroom[updateType]('students', students)
        [updateType]('name', classroomName)
        [updateType]('autoAssignNewEnrollees', autoAssignNewEnrollees);
    });
    /*
      If this was the last recipient of the classroom, remove the classroom
    */
    if (updatedSelectedRecipients.get(classroomId).isEmpty()) {
      updatedSelectedRecipients = updatedSelectedRecipients.delete(classroomId);
    }

    this.writeData('selectedRecipients', updatedSelectedRecipients);
  }

  _toggleAutoAssignNewEnrollees(recipientData: Map<string, *>) {
    const selectedRecipients = this.getSelectedRecipients();
    const classroomId = recipientData.get('classroomId');
    const autoAssignNewEnrollees = recipientData.get('autoAssignNewEnrollees');

    const updatedSelectedRecipients = selectedRecipients.setIn(
      [classroomId, 'autoAssignNewEnrollees'],
      autoAssignNewEnrollees
    );

    this.writeData('selectedRecipients', updatedSelectedRecipients);
  }

  async _addOutOfScopeSubjectsToClassrooms(hasLicense): Promise<*> {
    this.writeData('isAddSubjectsToOutOfScopeClassroomsPending', true);
    const classroomPromises = this.getClassroomsWithOutOfScopeSubjects().reduce(
      (acc, subjects, classroom) => {
        const relationship = {
          type: 'subjects_v2',
          relation: subjects
            .map((subject) => (typeof subject === 'string' ? subject : subject.getId()))
            .toJS()
        };
        if (hasLicense) {
          relationship.customMeta = subjects.reduce((prev, curr) => {
            const id = typeof curr === 'string' ? curr : curr.getId();
            return prev.set(id, fromJS({restrict_free_practice: true}));
          }, Map());
        }
        const classroomWithSubjects = classroom.addRelationship(relationship).save();
        acc.push(classroomWithSubjects);
        return acc;
      },
      []
    );
    const addOutOfScopeSubjectsToClassroomsPromise = Promise.all(classroomPromises);
    this.writeData(
      'addOutOfScopeSubjectsToClassroomsPromise',
      addOutOfScopeSubjectsToClassroomsPromise
    );
    await addOutOfScopeSubjectsToClassroomsPromise;
    this.writeData((store) =>
      store
        .set('showOutOfScopeSubjectModal', false)
        .set('isAddSubjectsToOutOfScopeClassroomsPending', false)
        .set('addOutOfScopeSubjectsToClassroomsPromise', null)
    );
  }

  /*
    In CreateAssignmentRecipientGroup, `activeGroup` will control
    which group component re-renders on classroom and student select.
  */
  _setActiveRecipientGroup(groupId: string) {
    this.writeData('activeRecipientGroup', groupId);
  }

  getActiveRecipientGroup(): string {
    return this.readData('activeRecipientGroup');
  }

  getSelectedRecipients(): Map<string, *> {
    return this.readData('selectedRecipients');
  }

  getSelectedRecipientsTotal(): number {
    return this.getSelectedRecipientSet().size;
  }

  getSelectedRecipientSet(): Set<string> {
    return this.getSelectedRecipients()
      .map((classroom) => {
        return classroom.get('students').reduce((result, student) => {
          return result.push(student.getId());
        }, new List());
      })
      .toList()
      .flatten()
      .toSet();
  }

  isAutoAssignNewClassroomEnrollees(): boolean {
    return this.getAssignmentModel().isAutoAssignNewClassroomEnrollees();
  }

  getClassroomsWithOutOfScopeSubjects(): Map<ClassroomModelV1, List<*>> {
    const selectedRecipientIds = this.getSelectedRecipients().keySeq().toSet();
    const selectedRecipientClasses = this.getMyClassrooms()
      .filter((classroom) => selectedRecipientIds.includes(classroom.getId()))
      .toSet();
    const questionSetSubjects = this.getQuestionSetList()
      .map((questionSet) => {
        let subject = questionSet.getSubject();
        if (subject.isEmpty()) {
          subject = questionSet.getSubjectId();
        }
        return subject;
      })
      .toSet();
    return selectedRecipientClasses.reduce((acc, classroom) => {
      const classroomSubjectIds = classroom.getSubjects().keySeq();
      const missingSubjects = questionSetSubjects.filterNot((subject) =>
        classroomSubjectIds.includes(typeof subject === 'string' ? subject : subject.getId())
      );
      return missingSubjects.isEmpty() ? acc : acc.set(classroom, missingSubjects);
    }, Map());
  }

  doSelectedClassroomsLackSubjectScope(): boolean {
    return !this.getClassroomsWithOutOfScopeSubjects().isEmpty();
  }

  shouldShowOutOfScopeSubjectModal = () => this.readData('showOutOfScopeSubjectModal');

  isAddSubjectsToOutOfScopeClassroomsPending = () =>
    this.readData('isAddSubjectsToOutOfScopeClassroomsPending');

  getAddOutOfScopeSubjectsToClassroomsPromise = () =>
    this.readData('addOutOfScopeSubjectsToClassroomsPromise');

  /*
    ASSIGNMENT OPTIONS HELPERS
  */
  _setFieldState(inputState: Map<*, *>) {
    /*
      inputState bears a `key` and `value` derived from e.target.name and e.target.value.

      `key` must match a key on the `assignment` Map.
    */
    const key = inputState.get('key');
    const value = inputState.get('value');

    const updatedModel = this.getAssignmentModel()[modelMethods.getIn([key, 'set'])](value);
    this.writeData('assignment', updatedModel);
  }

  _validateAssignment() {
    /*
      This validator method iterates through the inputKeys array (defined above).

      Every key has a twin on the object formValidator (defined below).
      Each calls a specific validator function built to assess the value of
      a given input.

      As we reduce over each of our inputKeys, we call the method located
      at that key on formValidator, and test the current input's value.

      For example, given the following, where `assignment` is our current instance of assignment model:

        const errorData = inputKeys.reduce((prev, curr) => {
          const value = assignment[this._getModelGetMethod(curr)]();
          const errorMessage = formValidator[curr](value, curr);
          return prev[errorMessage !== null ? 'set' : 'delete'](curr, errorMessage);
        }, new Map());

      formValidator['name'] calls the method at that key: in this case, genericValidator(),
      while passing two arguments: the key 'name' and the value of assignment.getName().

      The result of the method will set our `isAssignmentValid` flag either to true or false,
      and if false, will write an error message to error `errorData`, which we save to
      the store.
    */
    const assignment = this.getAssignmentModel();

    const errorData = inputKeys.reduce((prev, curr) => {
      /*
        What's happening here?

        We use the errorData Map to communicate with our components about any error messages
        that the user needs to receive.

        We use a reducer function to check each input with its validator and return
        the completed Map of errorData.

        However, we don't want to clutter it up with null values.

        Therefore, should a particular validator method return
        null for `errorMessage`, we test it with a simple ternary. The result of the ternary
        calls either `set or `delete` on the current property of the Map.
      */
      const validatorKey = modelMethods.getIn([curr, 'get']);
      const validatorFunc = assignment.validators[validatorKey];
      const errorMessage = validatorFunc()?.join('. ');
      return prev[errorMessage ? 'set' : 'delete'](curr, errorMessage);
    }, Map());

    this.writeData((store) => {
      /**
       * 99% of the cases this first gets called, it's when the user edits the title.
       * On those first passes, we mark the assignment invalid insofar as it's incomplete
       * but we suppress the due date error that will appear (at first).
       */
      if (store.get('assignment').getDueDate() === null) {
        return store.set('isAssignmentValid', false);
      }

      return store.set('errorData', errorData).set('isAssignmentValid', errorData.size === 0);
    });

    if (this.isAssigningPracticeExam()) {
      this._validateSectionTimeLimits();
    }
  }

  isAllowLateSubmissions(): boolean {
    return this.getAssignmentModel().isAllowLateSubmissions();
  }

  getCorrectAnswerSetting(): string {
    return this.getAssignmentModel().getCorrectAnswerSetting();
  }

  getDueDate(): moment {
    return this.getAssignmentModel().getDueDate();
  }

  getId(): string {
    return this.getAssignmentModel().getId();
  }

  getName(): string {
    return this.getAssignmentModel().getName();
  }

  getStartDate(): moment {
    return this.getAssignmentModel().getStartDate();
  }

  getTimeLimit(): number | null {
    return this.isAssigningPracticeExam()
      ? this.getQueryForAssessment().getResource().getTimeLimit()
      : this.getAssignmentModel().getTimeLimit();
  }

  getMessage(): string | null {
    return this.getAssignmentModel().getMessage();
  }

  getStudentIds(): List<string> {
    return this.getAssignmentModel().getStudentIds();
  }

  getStudents(): List<StudentModel> {
    return this.getAssignmentModel().getStudents();
  }

  getClassrooms(): List<ClassroomModelV1> {
    return this.getAssignmentModel().getClassrooms();
  }

  getAssignmentClassroomId(): string {
    return this.getClassrooms().first().getId();
  }

  getAssignmentClassroomName(): string {
    return this.getClassrooms().first().getName();
  }

  getErrorData(): Map<*, *> {
    return this.readData('errorData');
  }

  getIsAssignmentValid(): boolean {
    return this.readData('isAssignmentValid');
  }

  isRandomizeQuestionOrder(): boolean {
    return this.getAssignmentModel().isRandomizeQuestionOrder();
  }

  isAssignedToEntireClass() {
    /**
     * Warning: this only works when the teacher is assigning a single classroom
     */
    return this.getSelectedRecipients().reduce((_, classroom, id) => {
      const recipientsSelectedAmongEnrollees = classroom.get('students');
      const allClassroomEnrollees = this.getMyClassrooms()
        .find((cachedClassroom) => cachedClassroom.getId() === id)
        .getStudents();
      return allClassroomEnrollees.size === recipientsSelectedAmongEnrollees.size;
    }, false);
  }

  getAssignmentSettings() {
    const ltiLineItemId = this.getAssignmentModel().getLtiLineItemId();
    const message = this.getAssignmentModel().getMessage();
    const timeLimit = this.getAssignmentModel().getTimeLimit();

    const settings = {
      allow_late_submissions: this.getAssignmentModel().isAllowLateSubmissions(),
      correct_answer_setting: this.getAssignmentModel().getCorrectAnswerSetting(),
      due_date: this.getAssignmentModel().getDueDate(),
      randomize_question_order: this.getAssignmentModel().isRandomizeQuestionOrder(),
      start_date: this.getAssignmentModel().getStartDate() || 'now',
      // if these are empty strings or null, the backend errors
      ...(ltiLineItemId && {lti_line_item_id: ltiLineItemId}),
      ...(message && {message}),
      ...(timeLimit && {time_limit: timeLimit})
    };
    return settings;
  }

  _syncRosters = this.withSavedPromise(() => {
    /**
     * We refetch the classroom to guarantee we have the latest roster available
     * before saving the relationships between the students and their assignment,
     * except in edit mode
     *
     * @see https://github.com/albert-io/project-management/issues/3492
     */
    if (this.getIsEditMode()) {
      return Promise.resolve();
    }

    const promises = this.getSelectedRecipients()
      .keySeq()
      .reduce((prev, curr) => {
        const query = resource('students_v1')
          .mandarkEndpoint(['classrooms_v1', curr, 'students_v2'])
          .pageSize('infinite')
          .done();
        const promise = genericMandarkRequest('get', query);
        prev[curr] = promise;
        return prev;
      }, {});

    return Promise.all(Object.values(promises))
      .then((res) => {
        res.forEach((latestRoster, index) => {
          const classroomId = Object.keys(promises)[index];
          const autoAssignNewEnrollees = this.getSelectedRecipients().getIn(
            [classroomId, 'autoAssignNewEnrollees'],
            true
          );
          const assignees = this.getSelectedRecipients().getIn([classroomId, 'students'], List());
          const assigneeIds = assignees.map((student) => student.getId()).toSet();
          const cachedClassroom = this.getMyClassrooms().find(
            (classroom) => classroom.getId() === classroomId
          );
          const cachedEnrolleeIds = cachedClassroom
            .getStudents()
            .map((student) => student.getId())
            .toSet();
          const unassignedStudentIds = cachedEnrolleeIds.subtract(assigneeIds);
          const updatedStudents = latestRoster.filterNot((student) =>
            unassignedStudentIds.includes(student.getId())
          );

          const recipientData = Map({
            addRecipients: true,
            autoAssignNewEnrollees,
            classroomId,
            classroomName: cachedClassroom.get('name'),
            students: updatedStudents
          });
          this._setMultipleRecipients(recipientData);
        });
      })
      .catch((error) => {
        notifier.notify(error, {
          component: 'Assignment Creation Form',
          name: 'Failed to retrieve latest roster for classroom'
        });
      });
  }, 'syncRostersPromise');

  getSyncRostersPromise = () => this.readData('syncRostersPromise');

  async _saveAssignment() {
    if (this.getIsSavePending()) {
      return;
    }

    this.writeData('isSavePending', true);

    const promiseResponse = this.getIsEditMode()
      ? this.getSelectedRecipients()
          .map((classroom, id) => this._processSaveAssignment(classroom, id))
          .toArray()
      : genericMandarkRequest(
          'post',
          query()
            .mandarkEndpoint(['assignments_v3'])
            .customHeader({'Content-Type': 'application/vnd.api+json; ext=bulk'})
            .include('classrooms_v1.school_v5,students_v1')
            .withMeta('assignment_v3,classroom_v1,student_v1')
            .customQuery({
              meta: {
                context: {
                  lti_deployment: true,
                  lti_platform_name: true,
                  student: {
                    id: this.getTeacherId()
                  }
                }
              }
            })
            .done(),
          {
            data: this.getSelectedRecipients()
              .map((classroom, id) => this._createAssignmentPayload(classroom, id))
              .toArray()
          }
        ).then((assignmentResults) => {
          invalidatePartialInterest({resourcePath: ['assignments_v3']});

          // Invalidates classroom list view metadata query + index query
          invalidatePartialInterest({
            resourcePath: [
              'teachers_v1',
              masqueradeStore.getUserIdByMasqueradeState(),
              'classrooms_v1'
            ]
          });
          // Invalidates classroom by id query driving the teacher classroom detail view
          this.getSelectedRecipients().forEach((classroom, id) => {
            invalidatePartialInterest({resourcePath: ['classrooms_v1', id]});
            /*
            Store updates for front-end
          */
            this.writeData((store) => {
              return store.set('isSavePending', false).set('isSaveSuccessful', true);
            });
          });

          invalidateFilters();

          invalidateTeacherAssignmentStats();

          /**
           * If we're not editing, we send a SINGLE event for the assignment of the assignment.
           *
           * @memberof Analytics
           * @see https://github.com/albert-io/project-management/issues/3593
           */

          analytics.emit(analytics.EVENTS.ASSIGNMENT_ASSIGNED, {
            /**
             * This *must* send either `assessment` or `assignment`.
             */
            type: this.getAssignmentTypeLabel()
          });

          return assignmentResults;
        });

    this.writeData('promiseResponse', promiseResponse);
  }

  _createAssignmentPayload(classroom: Map<string, *>, classroomId: string) {
    const questionSets = this.getQuestionSetList();
    const autoAssignNewEnrollees = classroom.get('autoAssignNewEnrollees');
    const studentIds = classroom.get('students').map((student) => student.getId());
    const teacherId = this.getTeacherId();

    let assignmentModel = new AssignmentModelV3(this.getAssignmentModel());

    const {folderId} = appStore.routerProps.params;
    if (folderId) {
      assignmentModel = assignmentModel.setTemplateId(folderId);
    }

    /*
        If the user hasn't chosen a start date, we set one for them
        upon creation using the current date and time. (required)
      */
    const startDate = assignmentModel.get('start_date') || 'now';

    const questionSetCustomMeta = questionSets.reduce((result, questionSet, index) => {
      return result.set(questionSet.getId(), {position: index});
    }, Map());

    const settings = {
      ...this.getAssignmentSettings(),
      auto_assign_new_classroom_enrollees: autoAssignNewEnrollees
    };

    const classes = [classroomId];

    const classesCustomMeta = classes.reduce(
      (result, id) =>
        result.set(id, {
          settings: {
            type: 'classroom',
            settings
          }
        }),
      Map()
    );

    const payload = assignmentModel
      .setStartDate(startDate)
      .setAutoAssignNewClassroomEnrollees(autoAssignNewEnrollees)
      .setStatus('assigned')
      .setSettings({
        type: 'default',
        settings
      })
      .updateRelationship('teacher_v1', teacherId)
      .updateRelationship('question_sets_v1', questionSets, true, questionSetCustomMeta)
      .updateRelationship('classrooms_v1', classes, true, classesCustomMeta)
      .updateRelationship('students_v1', studentIds)
      .getConstructedPayload();

    return payload;
  }

  // path for editing assignments only, create works through bulk create, edit is singular
  async _processSaveAssignment(classroom: Map<string, *>, classroomId: string): Promise<*> | null {
    const questionSets = this.getQuestionSetList();
    const autoAssignNewEnrollees = classroom.get('autoAssignNewEnrollees');
    const classroomName = classroom.get('name');
    const studentIds = classroom.get('students').map((student) => student.getId());
    const teacherId = this.getTeacherId();
    /*
      While the creation script is running we set the isSavePending flag
      in order to temporarily lock the 'submit' button on the ACF.
    */
    this.writeData('isSavePending', true);

    try {
      let assignmentModel = this.getAssignmentModel();

      const {folderId} = appStore.routerProps.params;
      if (folderId) {
        assignmentModel = assignmentModel.setTemplateId(folderId);
      }

      /*
        If the user hasn't chosen a start date, we set one for them
        upon creation using the current date and time. (required)
      */
      const startDate = assignmentModel.get('start_date') || 'now';

      const questionSetCustomMeta = questionSets.reduce((result, questionSet, index) => {
        return result.set(questionSet.getId(), {position: index});
      }, Map());

      const settings = {
        ...this.getAssignmentSettings(),
        auto_assign_new_classroom_enrollees: autoAssignNewEnrollees
      };

      const classCustomMeta = Map({
        [classroomId]: {
          settings: {
            type: 'classroom',
            settings
          }
        }
      });

      const promiseResponse = await assignmentModel
        .setStartDate(startDate)
        .setAutoAssignNewClassroomEnrollees(autoAssignNewEnrollees)
        .setStatus('assigned')
        .setSettings({
          type: 'default',
          settings
        })
        .updateRelationship('question_sets_v1', questionSets, true, questionSetCustomMeta)
        .updateRelationship('classrooms_v1', [classroomId], true, classCustomMeta)
        .updateRelationship('students_v1', studentIds, true, null, false, true)
        .save({
          customQuery: {
            include: 'classrooms_v1.school_v5,students_v1',
            customQuery: {
              with_meta: 'assignment_v3,classroom_v1,student_v1',
              meta: {
                context: {
                  lti_deployment: true,
                  lti_platform_name: true,
                  student: {
                    id: teacherId
                  }
                }
              }
            }
          }
        });

      if (assignmentsListStore.getClassroomId() === classroomId) {
        // invalidate teacher assignments query
        invalidatePartialInterest({
          resourcePath: ['assignments_v3'],
          filter: {
            classrooms_v1: classroomId
          }
        });
      } else {
        invalidatePartialInterest({resourcePath: ['assignments_v3']});
      }

      // Invalidates classroom list view metadata query + index query
      invalidatePartialInterest({
        resourcePath: ['teachers_v1', masqueradeStore.getUserIdByMasqueradeState(), 'classrooms_v1']
      });
      // Invalidates classroom by id query driving the teacher classroom detail view
      invalidatePartialInterest({resourcePath: ['classrooms_v1', classroomId]});

      invalidateFilters();

      invalidateTeacherAssignmentStats();

      const updatedToastMessage = this.getToastMessages().push(
        `Your assignment for ${classroomName} has been saved.`
      );

      /*
        Store updates for front-end
      */
      this.writeData((store) => {
        return store
          .set('isSavePending', false)
          .set('toastMessage', updatedToastMessage)
          .set('isSaveSuccessful', true);
      });
      return promiseResponse;
    } catch (e) {
      logger.error('Failed saving assignment: %s', e.message || 'unknown error');
      const error = e.response.body.errors[0];
      let message = 'There was a problem with your submission. Please try again.';
      if (error.code === errorCodes.BAD_REQUEST.VALIDATION_ERROR.QUESTION_SET_IN_HIDDEN_SUBJECT) {
        message =
          'This assignment contains questions from a subject that has been unpublished. No new assignments can be created with these questions.';
      } else if (error.code === errorCodes.BAD_REQUEST.QUESTION_IS_ARCHIVED) {
        message =
          'This assignment contains questions that have been archived. No new assignments can be created with these questions.';
      } else if (error.code === errorCodes.BAD_REQUEST.QUESTION_IS_HIDDEN) {
        message =
          'This assignment contains questions that have been hidden. No new assignments can be created with these questions.';
      }
      const updatedToastMessage = this.getToastMessages().push(message);
      this.writeData((store) => {
        return store
          .set('isSavePending', false)
          .set('isSaveSuccessful', false)
          .set('toastMessage', updatedToastMessage);
      });

      return null;
    }
  }

  getPromiseResponse(): Array<*> | Promise<any> {
    return this.readData('promiseResponse');
  }

  getToastMessages(): List<*> {
    return this.readData('toastMessage');
  }

  getIsSavePending(): boolean {
    return this.readData('isSavePending');
  }

  getIsSaveSuccessful(): boolean {
    return this.readData('isSaveSuccessful');
  }

  getIsEditMode(): string {
    return this.readData('isEditMode');
  }

  _resetStore() {
    this.resetStore();
    this.writeData('assignment', AssignmentModelV3.getDefaultModel());
  }

  getInsertedAt(): moment | null {
    return this.getAssignmentModel().getInsertedAt();
  }

  hasStartDate(): boolean {
    /*
      We can infer that the assignment has no start date if its
      inserted_at date/updated_at date is identical to its start date.
      This suggests the start_date was created by default. We flag
      such dates as false positives and return "false" if such dates match.
    */
    const insertedAtDateAndStartDateAreSame = this.getInsertedAt().isSame(
      this.getStartDate(),
      'minute'
    );
    const updatedAtDateAndStartDateAreSame = this.getUpdatedAt().isSame(
      this.getStartDate(),
      'minute'
    );
    return !updatedAtDateAndStartDateAreSame && !insertedAtDateAndStartDateAreSame;
  }

  getUpdatedAt(): moment | null {
    return this.getAssignmentModel().getUpdatedAt();
  }

  getHasReceivedAssignmentData(): boolean {
    return this.readData('hasReceivedAssignmentData');
  }

  /**
   * Return the query for bootstrapping the ACF
   *
   * @param {Object} params
   * @param {string} params.assignmentId The route param telling us what kind of assignment to create.
   * @param {string} params.classroomId classroomId to filter by
   * @returns {Object} query builder object
   */
  getQueryForAssignment({assignmentId, classroomId}) {
    return mandarkEndpoint(['assignments_v3', assignmentId])
      .include('classrooms_v1.students_v1.student_assignments_v1')
      .include('question_sets_v1.questions_v1')
      .include('question_sets_v1.guide_levels_v2', this.getTeacherId())
      .include('question_sets_v1.subject_v2', this.getTeacherId())
      .include('students_v1')
      .filter({
        included: {
          'classrooms_v1.students_v1.student_assignments_v1': {
            id: assignmentId,
            teacher_v1: this.getTeacherId()
          },
          ...(classroomId && {
            classrooms_v1: {
              id: classroomId
            }
          })
        }
      });
  }

  /**
   * Return the query for bootstrapping the ACF
   *
   * @param {Object} params
   * @param {string} params.folderId The route param telling us what kind of assignment to create.
   * @returns {Object} query builder object
   */
  getQueryForFolder({folderId}) {
    return mandarkEndpoint(['templates_v1', folderId])
      .include('question_sets_v1.questions_v1')
      .include('question_sets_v1.guide_levels_v2', this.getTeacherId())
      .include('question_sets_v1.subject_v2', this.getTeacherId());
  }

  /**
   * Return the query for bootstrapping the ACF
   *
   * @param {Object} params
   * @param {string} params.subjectSlug The route param telling us what kind of assignment to create
   * @param {string} params.topicSlug The route param telling us what kind of assignment to create
   * @param {?string} params.questionSetSlug The route param telling us what kind of assignment to create
   * @returns {Object} query builder object
   */
  getQueryForSubjectSlug({subjectSlug, topicSlug, questionSetSlug}) {
    const requestFilter = questionSetSlug
      ? {slug_id: questionSetSlug}
      : {
          subject_v2: {
            url_slug: subjectSlug
          },
          guide_levels_v2: {
            url_slug: topicSlug
          }
        };

    return mandarkEndpoint(['question_sets_v1'])
      .include('guide_levels_v2')
      .include('questions_v1')
      .include('subject_v2')
      .filter(requestFilter)
      .sort('difficulty')
      .page(1)
      .pageSize(200);
  }

  getOriginalAssignment(): AssignmentModelV3 {
    return this.readData('originalAssignment');
  }

  // Practice exams
  /**
   * Return the query for bootstrapping the ACF
   *
   * @params {Object} param
   * @param params
   * @param {string} params.examId The route param telling us what kind of assignment to create.
   * @returns {Object} query builder object
   */
  getQueryForAssessment(params = appStore.routerProps.params) {
    const {examId} = params;
    return resource('exam_v1')
      .mandarkEndpoint(['exams_v1', examId])
      .include('sections_v1.question_sets_v1.questions_v1.supplements_v1')
      .include('sections_v1.question_sets_v1.subject_v2')
      .include('sections_v1.question_sets_v1.tags_v1')
      .include('question_sets_v1.questions_v1.supplements_v1')
      .include('question_sets_v1.subject_v2')
      .include('question_sets_v1.tags_v1')
      .include('subject_v2')
      .customQuery({
        question_v1: {
          labels: true
        }
      });
  }

  async _processSavePracticeExamAssignment(
    classroom: Map<string, *>,
    classroomId: string
  ): Promise<*> {
    this.writeData('isSavePending', true);
    try {
      const studentIds = classroom.get('students').map((student) => student.getId());
      const autoAssignNewEnrollees = classroom.get('autoAssignNewEnrollees');
      const assignmentData = this.getAssignmentModel();
      const startDate = assignmentData.getStartDate() || 'now';

      /**
       * We reset the change map before saving so when we post to /create_from_exam_template, our
       * assigned practice exam reuses as much as possible from the original practice exam template.
       */
      const assignmentToCreate = new AssignmentModelV3()
        .resetChangeMap()
        // Clear all values that were populated set by calling the constructor
        .setDueDate(null)
        .setStartDate(null)
        .setTimeLimit(null)
        // Begin setting bona fide values
        .setName(assignmentData.getName())
        .setStartDate(startDate)
        .setDueDate(assignmentData.getDueDate())
        .setStatus('assigned')
        .setMessage(assignmentData.getMessage())
        .setAllowLateSubmissions(assignmentData.isAllowLateSubmissions())
        .setCorrectAnswerSetting(assignmentData.getCorrectAnswerSetting())
        .setAutoAssignNewClassroomEnrollees(autoAssignNewEnrollees)
        .addRelationship({
          type: 'teacher_v1',
          relation: this.getTeacherId()
        })
        .addRelationship({
          type: 'classrooms_v1',
          relation: [classroomId]
        })
        .addRelationship({
          type: 'students_v1',
          relation: studentIds
        });

      const query = mandarkEndpoint([
        'assignments_v3',
        'create_from_exam_template',
        appStore.routerProps.params.examId
      ])
        .include('classrooms_v1')
        .include('question_sets_v1.questions_v1')
        .include('students_v2')
        .customQuery({
          with_meta: 'assignment_v3,classroom_v1,student_v2'
        })
        .done();

      const createdAssignment = await genericMandarkRequest(
        'post',
        query,
        assignmentToCreate.getConstructedPayload()
      );

      if (!this.getSections().isEmpty()) {
        await this._saveSectionTimeLimits(createdAssignment.getId());
      }

      if (assignmentsListStore.getClassroomId() === classroomId) {
        invalidatePartialInterest({resourcePath: ['assignments_v3']});
      }

      invalidateTeacherAssignmentStats();

      const updatedToastMessage = this.getToastMessages().push(
        `Your assignment for ${classroom.get('name')} has been saved.`
      );
      this.writeData((store) => {
        return store
          .set('isSavePending', false)
          .set('toastMessage', updatedToastMessage)
          .set('isSaveSuccessful', true);
      });

      const isScheduled = moment().isBefore(createdAssignment.getStartDate());
      analytics.emit(
        analytics.EVENTS.ASSIGNMENT_CREATED,
        createdAssignment.getQuestionSets().size,
        isScheduled
      );

      invalidatePartialInterest(mandarkEndpoint(['assignments_v3']).done());

      return createdAssignment;
    } catch (e) {
      const updatedToastMessage = this.getToastMessages().push(
        'There was a problem with your submission. Please try again.'
      );
      this.writeData((store) => {
        return store
          .set('isSavePending', false)
          .set('isSaveSuccessful', false)
          .set('toastMessage', updatedToastMessage);
      });

      return null;
    }
  }

  getSectionTimeLimitError(timeLimit: number): ?string {
    let errorMessage = null;
    // eslint-disable-next-line no-restricted-globals
    if (timeLimit !== null && (isNaN(timeLimit) || timeLimit <= 0)) {
      errorMessage = 'Time limit must be greater than 0';
    } else if (timeLimit > 60 * 24 * 7) {
      errorMessage = 'Time limit cannot exceed seven days';
    }
    return errorMessage;
  }

  async _saveSectionTimeLimits(createdAssignmentId: string): Promise<*> {
    const assignmentWithSections = await mandarkEndpoint(['assignments_v3', createdAssignmentId])
      .include('sections_v1')
      .getResourcePromise();
    const sections = assignmentWithSections.getSections();
    const customSections = this.getSections();
    for (const customSection of customSections) {
      // eslint-disable-next-line no-await-in-loop
      await sections
        .find((section) => section.getPosition() === customSection.getPosition())
        .setTimeLimit(customSection.getTimeLimit())
        .save();
    }
  }

  isAssigningPracticeExam(): boolean {
    return appStore.routerProps.params.examId !== undefined;
  }

  getAssignmentTypeLabel(): string {
    return this.isAssigningPracticeExam() ? 'assessment' : 'assignment';
  }

  _validateSectionTimeLimits() {
    const hasError = this.getSections().some((section) => !!section.validators.getTimeLimit());

    if (hasError) {
      this.writeData('isAssignmentValid', false);
    }
  }

  // Practice exams end

  _resetToastMessage() {
    this.writeData('toastMessage', new List());
  }

  _setSavedAssignmentPayload(payload: List<AssignmentModelV3>) {
    this.writeData('savedAssignmentPayload', payload);
  }

  getSavedAssignmentPayload(): List<AssignmentModelV3> {
    return this.readData('savedAssignmentPayload');
  }
}

export default new CreateAssignmentStore('CreateAssignmentStore');
